import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/domains/organizations';
import { useCampaigns } from '@/domains/campaigns';
import { brandRepository } from '../repositories/brand.repository';
import { brandService } from '../services/brand.service';
import { brandKeys } from '../services/brand.keys';
import type { BrandWithMetrics } from '../types/brand.types';

export type { BrandWithMetrics };

export function useAllBrandMetrics(showOnlyActive: boolean = false) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { data: campaigns, isLoading: campaignsLoading } = useCampaigns();

  const query = useQuery({
    queryKey: brandKeys.allMetrics(orgId),
    queryFn: async (): Promise<BrandWithMetrics[]> => {
      if (!orgId) {
        throw new Error('No current organization selected');
      }
      return brandRepository.getAllBrandMetrics(orgId, false);
    },
    enabled: !!orgId,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    if (!campaigns) return query.data;

    return brandService.overlayAllBrandMetrics(query.data, campaigns, showOnlyActive);
  }, [query.data, campaigns, showOnlyActive]);

  return {
    ...query,
    data,
    isLoading: query.isLoading || (campaignsLoading && !data),
  };
}
